import React, {useEffect, useState}
                                        from 'react'

import {PeopleButton}         from '../../lib/iprs-react-library/src/index.js'
import {MoreLess}             from '../../lib/iprs-react-library/src/index.js'
import TeamGroupTasksOverlay  from './TeamGroupTasksOverlay'
import TeamGroupAppointmentTypesOverlay
                              from './TeamGroupAppointmentTypesOverlay'
import {loadTeamGroups, UNGROUPED_KEY, UNGROUPED_TITLE}
                              from './teamGroups'
import {MdAssignmentTurnedIn, MdEventAvailable}
                              from 'react-icons/md'
import userLoggedInHasRole    from '../Util/userLoggedInHasRole'

/* Local icon button for the group shortcuts (tasks, appointment types),
 * matching the look of the buttonWrapper-built icons in
 * iprs-react-library/util/Buttons.js. */
const GroupIconButton = ({ onClick, title, Icon }) => {

    const handleClick = e => {
        e.preventDefault();
        onClick();
    }

    return <button type='button' onClick={handleClick} title={title}
        style={{ border: 0, backgroundColor: 'rgba(0,0,0,0)', padding: '5px 0',
            float: 'left', clear: 'none' }} >
        <Icon style={{ color: 'var(--colour__primary)' }} size='1.5rem' />
    </button>
}

/**
 * React component to display a selectable list of Teams. When team groups
 * have been set up (see TeamGroup maintenance in Settings), teams are shown
 * bucketed under collapsible group headers, the same way AppTypeSelectList
 * groups appointment types. Teams are bucketed by GroupID, with teams that
 * have no group under "Ungrouped". If no groups exist at all, falls back to
 * a flat list.
 * @returns {undefined}
 */
const TeamSelector = ({api, eventDispatch, state}) => {


    const {team: {teams, teamID, Groups = [], teamGroupLookup = {}, selectedGroupKey}, pageState }   = state

    const isLoggedIn = api.isLoggedIn();

    // only Diary Managers can change a group's task and appointment types,
    // so only they get the shortcut icons on the group headers
    const hasDiaryManagerPerm = userLoggedInHasRole(api, 'Diary Manager');

    // which group's task overlay is open, e.g. {groupID, groupTitle}, or null
    const [taskOverlayGroup, setTaskOverlayGroup] = useState(null);

    // which group's appointment type overlay is open, e.g. {groupID, groupTitle}, or null
    const [appTypeOverlayGroup, setAppTypeOverlayGroup] = useState(null);

    useEffect( () =>{
        if(!isLoggedIn) {return;}
        api.getTeamList()
            .then( response => response.apiResult )
            .then(
                teamList => {
                    if(teamList.length){

                        eventDispatch( {
                            menu: 'diary',
                            team: {
                                teams:      teamList,
                                teamID:     teamList[0].pkTeamId,
                                teamName:   teamList[0].TeamName
                            } } )
                    }
                }
            )
            .catch( err => console.log('Error loading team', err) )
    }, [eventDispatch, api, isLoggedIn])

    /* Loads the team group list and a teamID -> group lookup. Kept separate
     * from the flat team list above (which comes from the legacy
     * TeamList/usp_Team_List call and uses pkTeamId/TeamName) so that the
     * fields other components already rely on don't change shape. Settings
     * reloads this via the same loadTeamGroups after any change. */
    useEffect(() => {
        if (!isLoggedIn) { return; }

        loadTeamGroups(api, eventDispatch)
            .catch(err => console.log('Error loading team groups', err));
    }, [api, isLoggedIn, eventDispatch]);

    const handleSelectTeam = e => {
        e.preventDefault()

        const teamID    = Number.parseInt( e.target.name, 10 )
        const teamName  = teams.filter( t => t.pkTeamId === teamID )[0].TeamName

        eventDispatch( { team:    { teamID, teamName } } )
    }

    const handleEditClick = e => {

        e.preventDefault();

        eventDispatch( { team: {    teamID: Number.parseInt( e.target.value, 10 ),
                            editTeam:   true } } )
    }

    const handleGrpClick = (groupKey, show) => {

        /* NB: use the sentinel '__null__' rather than null/undefined here -
         * openGroupKey below falls back to the selected team's group via
         * ??, which treats null the same as "not set" and would silently
         * reopen the group instead of closing it. */
        eventDispatch({ team: { selectedGroupKey: show ? groupKey : '__null__' } });
    }

    const teamLink = (id, name) => {

        // only active if we are showing the team calendar and this is the current teamID
        const active = 'teamCal' === pageState && id === teamID


        return <li key={id}
            className={'sideMenuLink ' + (active? 'active': '') } >
            <a href='.' onClick={handleSelectTeam}
                name={id} className='sideMenuLink-anchor' >{name}</a>
            <div className='sideMenuButtons' >
                <PeopleButton value={id} onClick={handleEditClick} />
            </div>
        </li>
    }

    if (!Groups.length) {
        // no team groups set up yet, fall back to the flat, ungrouped list
        return (
            <ul>
                {[...teams]
                    .sort( (t1,t2) => t1.TeamName.localeCompare(t2.TeamName) )
                    .map( t => teamLink( t.pkTeamId, t.TeamName ) )
                }
            </ul>
        )
    }

    const groupKeyForTeam = id => teamGroupLookup[id]?.GroupID ?? UNGROUPED_KEY;

    const titleForGroupKey = groupKey => UNGROUPED_KEY === groupKey
        ? UNGROUPED_TITLE
        : Groups.find(g => g.GroupID === groupKey)?.GroupTitle ?? UNGROUPED_TITLE;

    const teamsByGroupKey = new Map();
    teams.forEach(t => {
        const groupKey = groupKeyForTeam(t.pkTeamId);
        if (!teamsByGroupKey.has(groupKey)) { teamsByGroupKey.set(groupKey, []); }
        teamsByGroupKey.get(groupKey).push(t);
    });

    const openGroupKey = selectedGroupKey ?? groupKeyForTeam(teamID);

    return (
        <>
        <ul>
            {[...teamsByGroupKey.keys()]
                .sort((a, b) => titleForGroupKey(a).localeCompare(titleForGroupKey(b)))
                .map(groupKey => (
                    <TeamGroup
                        key={groupKey}
                        groupKey={groupKey}
                        groupTitle={titleForGroupKey(groupKey)}
                        teamsInGroup={teamsByGroupKey.get(groupKey)}
                        show={groupKey === openGroupKey}
                        onGrpClick={handleGrpClick}
                        teamLink={teamLink}
                        showGroupIcons={hasDiaryManagerPerm}
                        onTasksClick={setTaskOverlayGroup}
                        onAppTypesClick={setAppTypeOverlayGroup}
                    />
                ))
            }
        </ul>
        {taskOverlayGroup &&
            <TeamGroupTasksOverlay
                api={api}
                groupID={taskOverlayGroup.groupID}
                groupTitle={taskOverlayGroup.groupTitle}
                onClose={() => setTaskOverlayGroup(null)}
            />
        }
        {appTypeOverlayGroup &&
            <TeamGroupAppointmentTypesOverlay
                api={api}
                groupID={appTypeOverlayGroup.groupID}
                groupTitle={appTypeOverlayGroup.groupTitle}
                onClose={() => setAppTypeOverlayGroup(null)}
            />
        }
        </>
    )
}

const TeamGroup = ({ groupKey, groupTitle, teamsInGroup, show, onGrpClick, teamLink, showGroupIcons,
    onTasksClick, onAppTypesClick }) => {

    const handleClick = () => onGrpClick(groupKey, !show);
    const handleTasksClick = () => onTasksClick({ groupID: groupKey, groupTitle });
    const handleAppTypesClick = () => onAppTypesClick({ groupID: groupKey, groupTitle });

    return <li>
        <div className='sideMenuLink'>
            <h3 onClick={handleClick} className="sideMenuLink-anchor" >{groupTitle} </h3>
            <nav className="sideMenuButtons sideMenuGroupButtons">
                {/* "Ungrouped" is a synthetic bucket, not a real TeamGroup, so it has no task or appointment type list */}
                {showGroupIcons && UNGROUPED_KEY !== groupKey && <>
                    <GroupIconButton onClick={handleTasksClick} title={`${groupTitle} tasks`}
                        Icon={MdAssignmentTurnedIn} />
                    <GroupIconButton onClick={handleAppTypesClick} title={`${groupTitle} appointment types`}
                        Icon={MdEventAvailable} />
                </>}
                <MoreLess onClick={handleClick} name={groupTitle} value={show} />
            </nav>
        </div>
        <div style={{ maxHeight: show ? 'fit-content' : '0', transition: 'all .1s', overflow: 'hidden' }} >
            <ul>
                {[...teamsInGroup]
                    .sort((t1, t2) => t1.TeamName.localeCompare(t2.TeamName))
                    .map(t => teamLink(t.pkTeamId, t.TeamName))
                }
            </ul>
        </div>
    </li>
}

export default TeamSelector
